'use client';

const TOKEN_KEY = 'dealer_portal_token';
const CONTEXT_KEY = 'dealer_portal_context';
const ROLE_KEY = 'dealer_portal_role';
const IMPERSONATION_KEY = 'dealer_portal_impersonation';

// ─── Token Management ────────────────────────────────────────

export function setToken(token: string): void {
    if (typeof window !== 'undefined') {
        localStorage.setItem(TOKEN_KEY, token);
        // Mirror to cookie for middleware access (server-side route protection)
        document.cookie = `dealer_portal_token=${token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
    }
}

export function getToken(): string | null {
    if (typeof window !== 'undefined') {
        return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    }
    return null;
}

export function removeToken(): void {
    if (typeof window !== 'undefined') {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(CONTEXT_KEY);
        localStorage.removeItem(ROLE_KEY);
        localStorage.removeItem(IMPERSONATION_KEY);
        localStorage.removeItem('dealer_logo');

        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(CONTEXT_KEY);
        sessionStorage.removeItem(ROLE_KEY);
        sessionStorage.removeItem(IMPERSONATION_KEY);
        sessionStorage.removeItem('dealer_portal_handoff');

        // Clear cookies too
        document.cookie = 'dealer_portal_token=; path=/; max-age=0';
        document.cookie = 'dealer_portal_role=; path=/; max-age=0';
        document.cookie = 'dealer_portal_impersonating=; path=/; max-age=0';
    }
}

// ─── Broker Context ──────────────────────────────────────────

export interface BrokerContextInfo {
    brokerId: string;
    brokerName: string;
    brokerDbaName?: string;
    dealerBrokerLinkId: string;
}

export function setBrokerContext(context: BrokerContextInfo): void {
    if (typeof window !== 'undefined') {
        localStorage.setItem(CONTEXT_KEY, JSON.stringify(context));
    }
}

export function getBrokerContext(): BrokerContextInfo | null {
    if (typeof window !== 'undefined') {
        const ssRaw = sessionStorage.getItem(CONTEXT_KEY);
        if (ssRaw) {
            try { return JSON.parse(ssRaw); } catch {}
        }
        const raw = localStorage.getItem(CONTEXT_KEY);
        if (raw) {
            try {
                return JSON.parse(raw);
            } catch {
                return null;
            }
        }
    }
    return null;
}

// ─── Authentication ──────────────────────────────────────────

export function isAuthenticated(): boolean {
    return !!getToken();
}

// ─── Role Management ─────────────────────────────────────────

export type UserRole = 'dealer' | 'broker' | 'superadmin';

export function setUserRole(role: UserRole): void {
    if (typeof window !== 'undefined') {
        localStorage.setItem(ROLE_KEY, role);
        // Mirror to cookie for middleware (server-side route guard)
        document.cookie = `dealer_portal_role=${role}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
    }
}

export function getUserRole(): UserRole {
    if (typeof window !== 'undefined') {
        const role = sessionStorage.getItem(ROLE_KEY) || localStorage.getItem(ROLE_KEY);
        if (role === 'superadmin') return 'superadmin';
        if (role === 'broker') return 'broker';
    }
    return 'dealer';
}

export function isSuperAdmin(): boolean {
    return getUserRole() === 'superadmin';
}

export function isBroker(): boolean {
    return getUserRole() === 'broker';
}

// ─── Impersonation (Broker → Dealer) ─────────────────────

export interface ImpersonationInfo {
    originalToken: string;
    originalRole: UserRole;
    brokerName: string;
    dealerName: string;
    dealerId: string;
}

export function setImpersonation(info: ImpersonationInfo): void {
    if (typeof window !== 'undefined') {
        localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(info));
    }
}

export function getImpersonationInfo(): ImpersonationInfo | null {
    if (typeof window !== 'undefined') {
        const ssRaw = sessionStorage.getItem(IMPERSONATION_KEY);
        if (ssRaw) {
            try { return JSON.parse(ssRaw); } catch {}
        }
        const raw = localStorage.getItem(IMPERSONATION_KEY);
        if (raw) {
            try {
                return JSON.parse(raw);
            } catch {
                return null;
            }
        }
    }
    return null;
}

export function isImpersonating(): boolean {
    return !!getImpersonationInfo();
}

/**
 * End impersonation: restore broker token and role, clear impersonation state.
 */
export function clearImpersonation(): void {
    if (typeof window !== 'undefined') {
        const info = getImpersonationInfo();
        if (info) {
            // Restore broker's original token and role ONLY if it existed
            if (info.originalToken) {
                setToken(info.originalToken);
            }
            if (info.originalRole) {
                setUserRole(info.originalRole);
            }
        }
        localStorage.removeItem(IMPERSONATION_KEY);
        
        sessionStorage.removeItem(IMPERSONATION_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(ROLE_KEY);
        sessionStorage.removeItem(CONTEXT_KEY);
        sessionStorage.removeItem('dealer_portal_handoff');
    }
}
